import moment from "moment";
import { CalendarDate } from "../calendar-date";
import { CalendarDateTime } from "../calendar-date-time";
const clientViewTimeoutInMinutes = 30;
const MinTime = Date.parse("1900-01-01 00:00");
export { MinTime };
const timeFormat = "h:mma";
const shortDateFormat = "M/D/YY";
const dateFormat = "M/D/YYYY";
const dateTimeFormat = dateFormat + " - " + timeFormat;
export const futureDate = new Date(Date.parse("3000-01-01"));
export const pastDate = new Date(Date.parse("1000-01-01"));
export function isValidDate(d) {
    const dateVal = moment(d, "L", true);
    return dateVal.isValid();
}
export function formatDate(d, blankValue = "-") {
    if (!d) {
        return blankValue;
    }
    return new Date(d).toLocaleString();
}
export function formattedDateTimeDefault(d) {
    return formattedDateTime(dateTimeFormat, d, "");
}
export function formatDateTimeInZone(utc, timezone) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZoneName: "short",
    }).formatToParts(utc);
    const pick = (type) => parts.find(p => p.type === type)?.value ?? "";
    const month = pick("month");
    const day = pick("day");
    const year = pick("year");
    const hour = pick("hour") === "24" ? "12" : pick("hour");
    const minute = pick("minute");
    const dayPeriod = pick("dayPeriod").toLowerCase();
    const zoneName = pick("timeZoneName");
    return `${month}/${day}/${year} - ${hour}:${minute}${dayPeriod} ${zoneName}`;
}
export function formattedDateTime(format, d, prefix = "on ") {
    if (!d) {
        return "at an unknown time";
    }
    return prefix + moment(d).format(format);
}
export function ago(d, prefix = "on ") {
    const formatString = "lll";
    return formattedDateTime(formatString, d, prefix);
}
export function daysAgo(d, prefix = "on ") {
    const formatString = "ll";
    return formattedDateTime(formatString, d, prefix);
}
export function relativeTimeAgo(d) {
    if (!d) {
        return "";
    }
    return moment(d).fromNow();
}
export function timeOnly(d, prefix = "") {
    return formattedDateTime(timeFormat, d, prefix);
}
export function formatDateToday() {
    return formatDateNoTime(moment());
}
export function formatDateNoTime(d, blankValue = "-") {
    if (!d) {
        return blankValue;
    }
    return moment(d).format(dateFormat);
}
export function formatMonthAndDay(d, blankValue = "-") {
    if (!d) {
        return blankValue;
    }
    return moment(d).format("MMM D");
}
export function formatCalendarDate(d, blankValue = "-") {
    if (d) {
        return d.humanFriendly();
    }
    return blankValue;
}
export function formatLongDateNoTime(d, blankValue = "-") {
    if (!d) {
        return blankValue;
    }
    return moment(d).format("MMMM Do YYYY");
}
export function formatDateHoursMinutes(d, blankValue = "-") {
    if (!d) {
        return blankValue;
    }
    return new Date(d).toLocaleString("en-us", {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}
export function inPast(d, basis = new Date()) {
    if (!d) {
        return false;
    }
    return new Date(d) < basis;
}
export function withinSevenDays(d, basis = new Date()) {
    if (!d) {
        return false;
    }
    const parsedDate = new Date(d);
    const sevenDays = moment(basis).add(7, "days").toDate();
    return parsedDate > basis && parsedDate <= sevenDays;
}
export function betweenOneAndTwoWeeks(d, basis = new Date()) {
    if (!d) {
        return false;
    }
    const parsedDate = new Date(d);
    const sevenDays = moment(basis).add(7, "days").toDate();
    const fourteenDays = moment(basis).add(14, "days").toDate();
    return parsedDate <= fourteenDays && parsedDate > sevenDays;
}
export function betweenTwoAndFourWeeks(d, basis = new Date()) {
    if (!d) {
        return false;
    }
    const parsedDate = new Date(d);
    const fourteenDays = moment(basis).add(14, "days").toDate();
    const twentyEightDays = moment(basis).add(28, "days").toDate();
    return parsedDate <= twentyEightDays && parsedDate > fourteenDays;
}
export function moreThanFourWeeks(d, basis = new Date()) {
    if (!d) {
        return false;
    }
    const parsedDate = new Date(d);
    const twentyEightDays = moment(basis).add(28, "days").toDate();
    return parsedDate > twentyEightDays;
}
export function lessThanNumDaysInPast(dateToCheck, nDays, basis = new Date()) {
    if (!dateToCheck) {
        return false;
    }
    const parsedDate = new Date(dateToCheck);
    const nDaysInPast = moment(basis).subtract(nDays, "days").toDate();
    return parsedDate > nDaysInPast;
}
export function viewTimeoutExpired(d, basis = new Date()) {
    if (!d) {
        return true;
    }
    const parsedDate = new Date(d);
    const expiresAfter = moment(parsedDate)
        .add(clientViewTimeoutInMinutes, "minutes")
        .toDate();
    return expiresAfter < basis;
}
export function dateSorterNewestToOldest(dateA, dateB) {
    if (!dateA && !dateB) {
        return 0;
    }
    if (!dateA) {
        return -1;
    }
    if (!dateB) {
        return 1;
    }
    return dateA.valueOf() - dateB.valueOf();
}
export function dateSorterOldestToNewest(dateA, dateB) {
    return -1 * dateSorterNewestToOldest(dateA, dateB);
}
export function yearsAgo(basis, years) {
    return moment(basis).subtract(years, "years").toDate();
}
export function getCalendarDateTime(calendarDate, date) {
    if (calendarDate) {
        return new CalendarDateTime(calendarDate);
    }
    if (date) {
        if (isNaN(date.getTime())) {
            return null;
        }
        return new CalendarDateTime(date);
    }
    return null;
}
// Displays time on today, otherwise display date
export function formatReminderDate(date) {
    const momentTime = moment(date.date);
    const isToday = momentTime.isSame(new Date(), "day");
    return isToday
        ? date.hasTime
            ? momentTime.format(timeFormat)
            : "today"
        : momentTime.format(shortDateFormat);
}
export function localCalendarDate() {
    const d = new Date();
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();
    return new CalendarDate(year, month, day);
}
export function getDaysDiff(date1, date2) {
    return Math.trunc((date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24));
}
// assumes Node.js's timezone is UTC
export function parseTimezonedDatetimeToUTC(datetime, timezone) {
    const localeString = datetime.toLocaleString("en-US", {
        timeZone: timezone || "Etc/UTC",
    });
    const targetDate = new Date(localeString);
    targetDate.setMilliseconds(datetime.getMilliseconds());
    const offsetDiff = datetime.getTime() - targetDate.getTime();
    return new Date(datetime.getTime() + offsetDiff);
}
// assumes Node.js's timezone is UTC
export function parseUTCDatetimeToTimezone(datetime, timezone) {
    const localeString = datetime.toLocaleString("en-US", {
        timeZone: timezone || "Etc/UTC",
    });
    const dateInUserTz = new Date(localeString);
    dateInUserTz.setMilliseconds(datetime.getMilliseconds());
    return dateInUserTz;
}
export function getCurrentTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
//# sourceMappingURL=date.js.map